import DashboardOutlined from "@mui/icons-material/DashboardOutlined";
import DirectionsBus from "@mui/icons-material/DirectionsBus";
import ConfirmationNumberOutlined from "@mui/icons-material/ConfirmationNumberOutlined";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import AltRouteOutlined from "@mui/icons-material/AltRouteOutlined";
import GroupOutlined from "@mui/icons-material/GroupOutlined";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import LocalAtmOutlined from "@mui/icons-material/LocalAtmOutlined";
import BarChartOutlined from "@mui/icons-material/BarChartOutlined";
import AttachMoney from "@mui/icons-material/AttachMoney";
import QrCodeScanner from "@mui/icons-material/QrCodeScanner";
import {
  getUserRole,
  isPlatformAdminUser,
  isTenantAdminUser,
  userHasAnyRoleOrGroup,
  userHasGroup,
} from "../utils/auth";

function isAdmin() {
  return userHasGroup("Company Admin");
}

function isDriverOnly() {
  return userHasGroup("Company Driver") && !isAdmin();
}

function isAgentOnly() {
  return (
    userHasAnyRoleOrGroup(["AGENT_MANAGER", "Agent Manager"]) && !isAdmin()
  );
}

export function getCabinetKind() {
  if (isAgentOnly()) return "agent";
  if (isDriverOnly()) return "driver";
  if (isPlatformAdminUser()) return "platform";
  if (isAdmin()) return "tenant";
  return "unknown";
}

export function getCabinetRoleLabelKey() {
  const role = getUserRole();
  if (role === "PLATFORM_ADMIN") return "admin.rolePlatform";
  if (role === "TENANT_ADMIN") return "admin.roleTenant";
  if (role === "DRIVER") return "admin.roleDriver";
  if (role === "AGENT_MANAGER") return "admin.roleAgent";
  if (role === "CASHIER") return "admin.roleCashier";
  const kind = getCabinetKind();
  if (kind === "platform") return "admin.rolePlatform";
  if (kind === "agent") return "admin.roleAgent";
  if (kind === "driver") return "admin.roleDriver";
  return "admin.roleAdmin";
}

export function getCabinetNavItems() {
  const kind = getCabinetKind();

  if (kind === "agent") {
    return [
      { to: "/agent", labelKey: "admin.nav.dashboard", icon: DashboardOutlined, match: "exact" },
      { to: "/agent/bookings", labelKey: "admin.bookings", icon: ConfirmationNumberOutlined },
      { to: "/agent/reconciliation", labelKey: "admin.reconciliationTitle", icon: FactCheckOutlined },
    ];
  }

  if (kind === "driver") {
    return [
      {
        to: "/administrator/trips/passengers",
        labelKey: "admin.nav.trips",
        icon: DirectionsBus,
        match: "trips",
      },
      { to: "/administrator/packages", labelKey: "header.packages", icon: Inventory2Outlined },
      { to: "/administrator/expenses", labelKey: "header.driverExpenses", icon: AttachMoney },
      { to: "/check-ticket", labelKey: "header.scanTicket", icon: QrCodeScanner },
    ];
  }

  const orgPath = "/administrator/organization";
  const items = [
    {
      to: "/administrator/dashboard",
      labelKey: "admin.nav.dashboard",
      icon: DashboardOutlined,
      match: "exact",
    },
    {
      to: "/administrator/trips/passengers",
      labelKey: "admin.nav.trips",
      icon: DirectionsBus,
      match: "trips",
    },
    { to: "/administrator/bookings", labelKey: "admin.bookings", icon: ConfirmationNumberOutlined },
    { to: "/administrator/packages", labelKey: "header.packages", icon: Inventory2Outlined },
    { divider: true, key: "ops-end" },
    {
      to: "/administrator/routes",
      labelKey: "admin.nav.routes",
      icon: AltRouteOutlined,
      match: "routes",
    },
  ];

  if (kind === "platform") {
    items.push({
      to: "/administrator/tenants",
      labelKey: "header.tenants",
      icon: BusinessOutlined,
      match: "tenantsList",
    });
  }

  if (kind === "platform" || isTenantAdminUser()) {
    items.push(
      { to: "/administrator/reconciliation", labelKey: "header.agentReconciliation", icon: FactCheckOutlined },
      { to: "/administrator/driver-cash", labelKey: "header.driverCash", icon: LocalAtmOutlined },
      { to: "/administrator/load-factor", labelKey: "header.loadFactor", icon: BarChartOutlined },
      { divider: true, key: "ops-reports-end" }
    );
  }

  items.push(
    {
      to: `${orgPath}?tab=staff`,
      labelKey: "admin.nav.staff",
      icon: GroupOutlined,
      match: "staff",
    },
    {
      to: orgPath,
      labelKey: "admin.nav.settings",
      icon: SettingsOutlined,
      match: "settings",
    }
  );

  return items;
}

export function isNavItemActive(item, pathname, search) {
  if (!item || item.divider) return false;
  const params = new URLSearchParams(search || "");
  const tab = params.get("tab");
  const onTenantDetail = /^\/administrator\/tenants\/[^/]+$/.test(pathname);
  const onOrgRedirect = pathname === "/administrator/organization";

  if (item.match === "exact") {
    return pathname === item.to.split("?")[0];
  }
  if (item.match === "trips") {
    return pathname.startsWith("/administrator/trips");
  }
  if (item.match === "routes") {
    return pathname.startsWith("/administrator/routes");
  }
  if (item.match === "tenantsList") {
    return pathname === "/administrator/tenants";
  }
  if (item.match === "staff") {
    return (onTenantDetail || onOrgRedirect) && tab === "staff";
  }
  if (item.match === "settings") {
    return (onTenantDetail || onOrgRedirect) && tab !== "staff";
  }
  const path = item.to.split("?")[0];
  return pathname === path || pathname.startsWith(`${path}/`);
}
